<script setup>
import { computed, watch } from 'vue';
import { formataDataFromDb } from '@/commons/utils';
import { useBasicosProjetos } from '@/modules/projeto/composables/useProjeto';

/**
 * Dados do contrato dinâmico da bolsa eventual (atividade, modalidade e vigência).
 *
 * Exibe só o que vier preenchido: solicitações feitas com o contrato em PDF não têm
 * esses campos, então o bloco some sozinho sem depender da flag do ambiente — e uma
 * solicitação criada no modo dinâmico continua exibindo os dados mesmo que a flag mude.
 */
const props = defineProps({
    // Objeto `dados` da solicitação.
    dados: {
        type: Object,
        default: () => ({}),
    },
});

const { listaModalidadeBolsa, carregarModalidadeBolsa } = useBasicosProjetos();

// O nome pode não vir junto com o id: nesse caso é resolvido pela lista de modalidades.
const nomeModalidade = computed(() => {
    const nomeRecebido = props.dados?.modalidade_bolsa_nome || props.dados?.modalidade_bolsa;
    if (nomeRecebido) return nomeRecebido;

    const id = props.dados?.modalidade_bolsa_id;
    if (!id) return null;

    return listaModalidadeBolsa.value.find((modalidade) => String(modalidade.id) === String(id))?.nome ?? null;
});

const vigencia = computed(() => {
    const { vigencia_inicio: inicio, vigencia_fim: fim } = props.dados ?? {};
    if (!inicio || !fim) return null;

    return `${formataDataFromDb(inicio)} até ${formataDataFromDb(fim)}`;
});

const temDadosContrato = computed(() => !!(props.dados?.atividade || nomeModalidade.value || vigencia.value));

// Nas telas de pagamento a solicitação chega depois da montagem, então a busca
// acompanha o id em vez de rodar uma vez só.
watch(
    () => props.dados?.modalidade_bolsa_id,
    async (id) => {
        const precisaDaLista = id
            && !props.dados?.modalidade_bolsa_nome
            && !props.dados?.modalidade_bolsa
            && !listaModalidadeBolsa.value.length;

        if (precisaDaLista) await carregarModalidadeBolsa();
    },
    { immediate: true },
);
</script>

<template>
    <template v-if="temDadosContrato">
        <LabelDados v-if="nomeModalidade" titulo="Modalidade da Bolsa" :conteudo="nomeModalidade" />

        <LabelDados v-if="vigencia" titulo="Vigência" :conteudo="vigencia" />

        <LabelDados v-if="props.dados.atividade" titulo="Atividade" :conteudo="props.dados.atividade"
            tamanho_div="col-12" />
    </template>
</template>
